import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner} from '@primer/react'
import type {ReactNode, Ref} from 'react'
import {Profiler, useEffect, useMemo, useState} from 'react'
import styles from './IpmBannerBase.module.css'

export interface IpmBannerBaseProps {
  dismissPath: string
  visibleEvent: string
  dismissEvent: string
  testId: string
  title: string
  icon?: ReactNode
  description: ReactNode
  primaryAction?: ReactNode
  variant?: 'critical' | 'info' | 'success' | 'upsell' | 'warning'
  rounded?: boolean
  location: string
  telemetryTags?: IpmMetricTags
  ref?: Ref<HTMLDivElement>
}

export function IpmBannerBase({
  dismissPath,
  visibleEvent,
  dismissEvent,
  testId,
  title,
  icon,
  description,
  primaryAction,
  variant,
  rounded,
  location,
  telemetryTags: telemetryTagsProp,
  ref,
}: IpmBannerBaseProps) {
  const [hidden, setHidden] = useState<boolean>(false)
  const isRounded = rounded ?? location !== 'global'
  const defaultTelemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'IpmBannerBase',
      surface: 'banner',
      display_location: location,
      variant: variant ?? 'default',
    }),
    [location, variant],
  )
  const telemetryTags = telemetryTagsProp ?? defaultTelemetryTags
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    if (telemetryTagsProp) {
      reportIpmVisible(telemetryTags)
    }
  }, [telemetryTags, telemetryTagsProp])

  const handleDismiss = async () => {
    if (telemetryTagsProp) {
      reportIpmDismiss(telemetryTags)
    }

    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      if (telemetryTagsProp) {
        reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      }
      // Silently handle network errors to maintain a good UX
    } finally {
      setHidden(true)
    }
  }

  if (hidden) {
    return null
  }

  const supportsCustomIcon = !variant || variant === 'info' || variant === 'upsell'

  const banner = (
    <div
      ref={ref}
      tabIndex={-1}
      data-hpc
      data-testid={testId}
      data-analytics-event={dismissEvent}
      data-analytics-visible={visibleEvent}
    >
      <Banner
        title={title}
        hideTitle
        leadingVisual={supportsCustomIcon ? icon : undefined}
        variant={variant}
        data-testid={`${testId}-inner`}
        className={isRounded ? undefined : styles.squareBanner}
        description={description}
        primaryAction={primaryAction}
        onDismiss={handleDismiss}
      />
    </div>
  )

  const content = !renderTiming.isEnabled ? (
    banner
  ) : (
    <Profiler id="IpmBannerBase" onRender={renderTiming.onRender}>
      {banner}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="IpmBannerBase">
      {content}
    </IpmErrorBoundary>
  )
}
